"use client";

import {
  getCookieAjsAnonymousId,
  getDeviceType,
  getDeviceUserAgent,
  getFullName,
  setCookieAjsAnonymousId,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { LDContext } from "launchdarkly-react-client-sdk";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useSearchParams } from "next/navigation";
import type { ComponentProps } from "react";
import { useEffect, useMemo, useState } from "react";

import { abContext } from "../lib";
import { getSearchParamsLDAttributes } from "../utils";

type LDContexts = Partial<Record<"device" | "guest" | "user", LDContext>>;

export type ABContextProviderProps = Omit<
  ComponentProps<typeof abContext.Provider>,
  "value"
>;

export const ABContextProvider = ({
  children,
  ...props
}: ABContextProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  /**
   * Keep track of what contexts have been identified
   * ldClient.identify() requires explicit contexts
   */
  const [wasDevice, setWasDevice] = useState(false);
  const [wasGuest, setWasGuest] = useState(false);
  const [wasUser, setWasUser] = useState(false);

  const searchParams = useSearchParams();

  const ldClient = useLDClient();

  const { query: queryMe } = useRequestMe();
  const { status } = useSession();
  const isQueryMeSessionLoading =
    status === "loading" ||
    (queryMe.isPending && queryMe.fetchStatus !== "idle");

  const ajsAnonymousId = getCookieAjsAnonymousId();
  const isWindowExists = typeof window !== "undefined";

  const ldContexts = useMemo<LDContexts>(() => {
    const contexts: LDContexts = {};
    const ldContextAttributesGlobal = {
      platform: "web",
    };

    /**
     * In rare situations, LD may initialize before Segment
     * In this case, create a LD anonymous device context
     * Which will auto-generate a UUID to use as "ajs_anonymous_id" cookie
     * That Segment will use when it initializes
     */
    if (wasDevice)
      contexts.device = {
        anonymous: true,
        ...ldContextAttributesGlobal,
      };

    const ldContextAttributes: LDContext = {
      anonymous: false,
      apiVersion: process.env.NEXT_PUBLIC_X_HALLOW_VERSION,
      browser: getDeviceUserAgent(),
      deviceType: getDeviceType(),
      preferredLocales: isWindowExists ? window?.navigator?.languages : [],
      screenHeight: isWindowExists ? window?.innerHeight : 0,
      screenWidth: isWindowExists ? window?.innerWidth : 0,
      ...getSearchParamsLDAttributes({ searchParams }),
      ...ldContextAttributesGlobal,
    };

    if (wasGuest)
      contexts.guest = {
        key: ajsAnonymousId,
        ...ldContextAttributes,
      };

    if (wasUser)
      contexts.user = {
        key: queryMe.data?.id?.toString(),
        ...ldContextAttributes,
        email: queryMe.data?.email,
        firstName: queryMe.data?.name,
        ipCountry: queryMe.data?.country,
        lastName: queryMe.data?.last_name,
        locale: queryMe.data?.language,
        name: getFullName({
          firstName: queryMe.data?.name,
          lastName: queryMe.data?.last_name,
        }),
      };

    return contexts;
  }, [wasDevice, wasGuest, wasUser]);

  /**
   * Determine if any new contexts require identification
   */
  useEffect(() => {
    if (!isQueryMeSessionLoading) {
      if (queryMe.data?.id && !wasUser) setWasUser(true);

      if (ajsAnonymousId && !wasGuest) setWasGuest(true);
      else if (!wasDevice) setWasDevice(true);
    }
  }, [ajsAnonymousId, isQueryMeSessionLoading, queryMe.data?.id]);

  /**
   * If LD initialized before consent/Segment
   * Keep LD's auto-generated "ajs_anonymous_id" cookie
   */
  useEffect(() => {
    if (ldClient && Object.keys(ldContexts).includes("device"))
      if (!ajsAnonymousId)
        setCookieAjsAnonymousId({ value: ldClient.getContext().key });
  }, [ldClient]);

  /**
   * Identify all contexts
   */
  useEffect(() => {
    if (Object.keys(ldContexts).length > 0)
      ldClient?.identify({ kind: "multi", ...ldContexts }, null, () => {
        if (isLoading) setIsLoading(false);
      });
  }, [ldContexts]);

  return (
    <abContext.Provider value={{ isLoading }} {...props}>
      {children}
    </abContext.Provider>
  );
};
